import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { examAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiSave, FiX, FiArrowLeft } from 'react-icons/fi';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Select from 'react-select';

const classicToolbarConfig = {
  items: [
    'heading', '|',
    'bold', 'italic', 'underline', 'subscript', 'superscript', '|',
    'bulletedList', 'numberedList', '|',
    'insertTable', '|',
    'link', 'blockQuote', '|',
    'undo', 'redo'
  ],
  shouldNotGroupWhenFull: true
};

const isRichTextEmpty = (html) => {
  if (!html || typeof html !== 'string') return true;
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  return text.length === 0;
};

const htmlToPlainPreview = (html, maxLen = 80) => {
  if (!html) return '';
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
};

const AddQuestion = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isViewMode = window.location.pathname.includes('/view/');
  const isEditMode = window.location.pathname.includes('/edit/');
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Math equation modal
  const [showMathModal, setShowMathModal] = useState(false);
  const [mathInput, setMathInput] = useState('');
  const [mathTarget, setMathTarget] = useState(''); // 'question' or 'explanation'
  const questionEditorRef = useRef(null);
  const explanationEditorRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    tradegroup: '',
    trade: '',
    subject: '',
    chapter: '',
    topic: '',
    question: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    option_e: '',
    answer: '',
    explanation: ''
  });
  
  // Master data
  const [tradegroups, setTradegroups] = useState([]);
  const [trades, setTrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    fetchTradeGroups();
    if (id) {
      fetchQuestion();
    }
    
    // Trigger MathJax rendering
    if (window.MathJax) {
      window.MathJax.typesetPromise();
    }
  }, [id]);

  // Load data dynamically based on selection
  useEffect(() => {
    if (formData.tradegroup) {
      fetchTrades(formData.tradegroup);
    }
  }, [formData.tradegroup]);

  useEffect(() => {
    if (formData.tradegroup && formData.trade) {
      fetchSubjects(formData.tradegroup, formData.trade);
    }
  }, [formData.tradegroup, formData.trade]);

  useEffect(() => {
    if (formData.tradegroup && formData.trade && formData.subject) {
      fetchChapters(formData.tradegroup, formData.trade, formData.subject);
    }
  }, [formData.tradegroup, formData.trade, formData.subject]);

  useEffect(() => {
    if (formData.tradegroup && formData.trade && formData.subject && formData.chapter) {
      fetchTopics(formData.tradegroup, formData.trade, formData.subject, formData.chapter);
    }
  }, [formData.tradegroup, formData.trade, formData.subject, formData.chapter]);

  const fetchTradeGroups = async () => {
    try {
      const response = await masterAPI.getTradeGroups();
      if (response.data.success) setTradegroups(response.data.data);
    } catch (error) {
      console.error('Error fetching trade groups:', error);
    }
  };

  const fetchTrades = async (tradegroupId) => {
    try {
      const response = await masterAPI.getTradesByGroup(tradegroupId);
      if (response.data.success) setTrades(response.data.data);
    } catch (error) {
      console.error('Error fetching trades:', error);
    }
  };

  const fetchSubjects = async (tradegroup, trade) => {
    try {
      const response = await masterAPI.getSubjectsByTrade(tradegroup, trade);
      if (response.data.success) setSubjects(response.data.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchChapters = async (tradegroup, trade, subject) => {
    try {
      const response = await masterAPI.getChaptersBySubject(tradegroup, trade, subject);
      if (response.data.success) setChapters(response.data.data);
    } catch (error) {
      console.error('Error fetching chapters:', error);
    }
  };

  const fetchTopics = async (tradegroup, trade, subject, chapter) => {
    try {
      const response = await masterAPI.getTopicsByChapter(tradegroup, trade, subject, chapter);
      if (response.data.success) setTopics(response.data.data);
    } catch (error) {
      console.error('Error fetching topics:', error);
    }
  };

  const fetchQuestion = async () => {
    setLoading(true);
    try {
      const response = await examAPI.getQuestion(id);
      if (response.data.success) {
        const q = response.data.data;
        setFormData({
          tradegroup: q.tradegroup || '',
          trade: q.trade || '',
          subject: q.subject || '',
          chapter: q.chapter || '',
          topic: q.topic || '',
          question: q.question || '',
          option_a: q.opt_a || '',
          option_b: q.opt_b || '',
          option_c: q.opt_c || '',
          option_d: q.opt_d || '',
          option_e: q.opt_e || '',
          answer: q.correct ? q.correct.toLowerCase() : '',
          explanation: q.explanation || ''
        });
      }
    } catch (error) {
      toast.error('Error loading question');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openMathModal = (target) => {
    setMathTarget(target);
    setMathInput('');
    setShowMathModal(true);
  };

  const insertMathEquation = () => {
    if (!mathInput.trim()) {
      toast.error('Please enter a math equation');
      return;
    }

    // Create the math equation HTML with MathJax delimiters
    const mathHtml = `<span class="math-equation">\\(${mathInput}\\)</span>`;
    
    if (mathTarget === 'question') {
      setFormData(prev => ({ 
        ...prev, 
        question: prev.question + mathHtml 
      }));
    } else if (mathTarget === 'explanation') {
      setFormData(prev => ({ 
        ...prev, 
        explanation: prev.explanation + mathHtml 
      }));
    }

    setShowMathModal(false);
    setMathInput('');
    
    // Trigger MathJax to render the new equation
    if (window.MathJax) {
      window.MathJax.typesetPromise();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Reset dependent dropdowns and clear their data - combine into single state update
    if (name === 'tradegroup') {
      setFormData(prev => ({ ...prev, tradegroup: value, trade: '', subject: '', chapter: '', topic: '' }));
      setTrades([]);
      setSubjects([]);
      setChapters([]);
      setTopics([]);
    } else if (name === 'trade') {
      setFormData(prev => ({ ...prev, trade: value, subject: '', chapter: '', topic: '' }));
      setSubjects([]);
      setChapters([]);
      setTopics([]);
    } else if (name === 'subject') {
      setFormData(prev => ({ ...prev, subject: value, chapter: '', topic: '' }));
      setChapters([]);
      setTopics([]);
    } else if (name === 'chapter') {
      setFormData(prev => ({ ...prev, chapter: value, topic: '' }));
      setTopics([]);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isRichTextEmpty(formData.question) || isRichTextEmpty(formData.option_a) || isRichTextEmpty(formData.option_b) || !formData.answer) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      let response;
      if (isEditMode && id) {
        response = await examAPI.updateQuestion(id, formData);
      } else {
        response = await examAPI.createQuestion(formData);
      }

      if (response.data.success) {
        toast.success(response.data.message || 'Question saved successfully');
        
        // For add mode, only clear question-specific fields, keep category/trade/subject/chapter/topic
        if (!isEditMode) {
          setFormData(prev => ({
            ...prev,
            question: '',
            option_a: '',
            option_b: '',
            option_c: '',
            option_d: '',
            option_e: '',
            answer: '',
            explanation: ''
          }));
        } else {
          // For edit mode, navigate back
          navigate('/questions');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving question');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter options based on selections
  const filteredTrades = useMemo(() => {
    if (!formData.tradegroup) return trades;
    return trades.filter(t => t.tradegroup_id == formData.tradegroup);
  }, [trades, formData.tradegroup]);

  const filteredSubjects = useMemo(() => {
    if (!formData.trade) return subjects;
    return subjects.filter(s => s.trade_id == formData.trade);
  }, [subjects, formData.trade]);

  const filteredChapters = useMemo(() => {
    if (!formData.subject) return chapters;
    return chapters.filter(c => c.subject_id == formData.subject);
  }, [chapters, formData.subject]);

  const filteredTopics = useMemo(() => {
    if (!formData.chapter) return topics;
    return topics.filter(t => t.chapter_id == formData.chapter);
  }, [topics, formData.chapter]);

  // Custom styles for react-select
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      padding: '2px',
      borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
      '&:hover': { borderColor: '#9ca3af' },
      borderRadius: '0.5rem',
      minHeight: '42px'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      zIndex: 50
    })
  };

  // Transform data for react-select format
  const tradegroupOptions = useMemo(() => 
    tradegroups.map(tg => ({ value: tg.id, label: tg.name })),
    [tradegroups]
  );

  const tradeOptions = useMemo(() => 
    trades.map(t => ({ value: t.id, label: t.name })),
    [trades]
  );

  const subjectOptions = useMemo(() => 
    subjects.map(s => ({ value: s.id, label: s.name })),
    [subjects]
  );

  const chapterOptions = useMemo(() => 
    chapters.map(c => ({ value: c.id, label: c.name })),
    [chapters]
  );

  const topicOptions = useMemo(() => 
    topics.map(t => ({ value: t.id, label: t.name })),
    [topics]
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/questions')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft size={20} />
          Back to Question Bank
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          {isViewMode ? 'View Question' : isEditMode ? 'Edit Question' : 'Add New Question'}
        </h1>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Hierarchy Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Classification</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-600">*</span>
              </label>
              <Select
                value={tradegroupOptions.find(opt => opt.value === formData.tradegroup) || null}
                onChange={(selectedOption) => {
                  const event = {
                    target: {
                      name: 'tradegroup',
                      value: selectedOption?.value || ''
                    }
                  };
                  handleInputChange(event);
                }}
                options={tradegroupOptions}
                placeholder="Select Category"
                isClearable
                isSearchable
                isDisabled={isViewMode}
                styles={customSelectStyles}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trade <span className="text-red-600">*</span>
              </label>
              <Select
                value={tradeOptions.find(opt => opt.value === formData.trade) || null}
                onChange={(selectedOption) => {
                  const event = {
                    target: {
                      name: 'trade',
                      value: selectedOption?.value || ''
                    }
                  };
                  handleInputChange(event);
                }}
                options={tradeOptions}
                placeholder="Select Trade"
                isClearable
                isSearchable
                isDisabled={isViewMode || !formData.tradegroup}
                styles={customSelectStyles}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject (Optional)</label>
              <Select
                value={subjectOptions.find(opt => opt.value === formData.subject) || null}
                onChange={(selectedOption) => {
                  const event = {
                    target: {
                      name: 'subject',
                      value: selectedOption?.value || ''
                    }
                  };
                  handleInputChange(event);
                }}
                options={subjectOptions}
                placeholder="Select Subject"
                isClearable
                isSearchable
                isDisabled={isViewMode || !formData.trade}
                styles={customSelectStyles}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Chapter (Optional)</label>
              <Select
                value={chapterOptions.find(opt => opt.value === formData.chapter) || null}
                onChange={(selectedOption) => {
                  const event = {
                    target: {
                      name: 'chapter',
                      value: selectedOption?.value || ''
                    }
                  };
                  handleInputChange(event);
                }}
                options={chapterOptions}
                placeholder="Select Chapter"
                isClearable
                isSearchable
                isDisabled={isViewMode || !formData.tradegroup || !formData.trade || !formData.subject}
                styles={customSelectStyles}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Topic (Optional)</label>
              <Select
                value={topicOptions.find(opt => opt.value === formData.topic) || null}
                onChange={(selectedOption) => {
                  const event = {
                    target: {
                      name: 'topic',
                      value: selectedOption?.value || ''
                    }
                  };
                  handleInputChange(event);
                }}
                options={topicOptions}
                placeholder="Select Topic"
                isClearable
                isSearchable
                isDisabled={isViewMode || !formData.tradegroup || !formData.trade || !formData.subject || !formData.chapter}
                styles={customSelectStyles}
              />
            </div>
          </div>
        </div>

        {/* Question Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Question Details</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Question <span className="text-red-600">*</span>
            </label>
            {isViewMode ? (
              <div 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                dangerouslySetInnerHTML={{ __html: formData.question }}
              />
            ) : (
              <>
                
                <div className="border border-gray-300 rounded-lg">
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.question}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setFormData(prev => ({ ...prev, question: data }));
                    }}
                    onReady={(editor) => {
                      questionEditorRef.current = editor;
                    }}
                    config={{
                      placeholder: 'Enter your question here...',
                      toolbar: {
                        items: [
                          'heading', '|',
                          'bold', 'italic', 'underline', 'subscript', 'superscript', '|',
                          'bulletedList', 'numberedList', '|',
                          'insertTable', '|',
                          'link', 'blockQuote', '|',
                          'undo', 'redo'
                        ],
                        shouldNotGroupWhenFull: true
                      }
                    }}
                  />
                </div>
                
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Option A <span className="text-red-600">*</span>
              </label>
              {isViewMode ? (
                <div
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 min-h-[3rem]"
                  dangerouslySetInnerHTML={{ __html: formData.option_a }}
                />
              ) : (
                <div className="border border-gray-300 rounded-lg">
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.option_a}
                    onChange={(event, editor) => {
                      setFormData(prev => ({ ...prev, option_a: editor.getData() }));
                    }}
                    config={{
                      placeholder: 'Option A…',
                      toolbar: classicToolbarConfig
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Option B <span className="text-red-600">*</span>
              </label>
              {isViewMode ? (
                <div
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 min-h-[3rem]"
                  dangerouslySetInnerHTML={{ __html: formData.option_b }}
                />
              ) : (
                <div className="border border-gray-300 rounded-lg">
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.option_b}
                    onChange={(event, editor) => {
                      setFormData(prev => ({ ...prev, option_b: editor.getData() }));
                    }}
                    config={{
                      placeholder: 'Option B…',
                      toolbar: classicToolbarConfig
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Option C</label>
              {isViewMode ? (
                <div
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 min-h-[3rem]"
                  dangerouslySetInnerHTML={{ __html: formData.option_c }}
                />
              ) : (
                <div className="border border-gray-300 rounded-lg">
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.option_c}
                    onChange={(event, editor) => {
                      setFormData(prev => ({ ...prev, option_c: editor.getData() }));
                    }}
                    config={{
                      placeholder: 'Option C (optional)…',
                      toolbar: classicToolbarConfig
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Option D</label>
              {isViewMode ? (
                <div
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 min-h-[3rem]"
                  dangerouslySetInnerHTML={{ __html: formData.option_d }}
                />
              ) : (
                <div className="border border-gray-300 rounded-lg">
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.option_d}
                    onChange={(event, editor) => {
                      setFormData(prev => ({ ...prev, option_d: editor.getData() }));
                    }}
                    config={{
                      placeholder: 'Option D (optional)…',
                      toolbar: classicToolbarConfig
                    }}
                  />
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Option E</label>
              {isViewMode ? (
                <div
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 min-h-[3rem]"
                  dangerouslySetInnerHTML={{ __html: formData.option_e }}
                />
              ) : (
                <div className="border border-gray-300 rounded-lg">
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.option_e}
                    onChange={(event, editor) => {
                      setFormData(prev => ({ ...prev, option_e: editor.getData() }));
                    }}
                    config={{
                      placeholder: 'Option E (optional)…',
                      toolbar: classicToolbarConfig
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Correct Answer <span className="text-red-600">*</span>
            </label>
            <select
              name="answer"
              value={formData.answer}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
              required
              disabled={isViewMode}
            >
              
              <option value="">Select Correct Answer</option>
              {!isRichTextEmpty(formData.option_a) && (
                <option value="a">A — {htmlToPlainPreview(formData.option_a)}</option>
              )}
              {!isRichTextEmpty(formData.option_b) && (
                <option value="b">B — {htmlToPlainPreview(formData.option_b)}</option>
              )}
              {!isRichTextEmpty(formData.option_c) && (
                <option value="c">C — {htmlToPlainPreview(formData.option_c)}</option>
              )}
              {!isRichTextEmpty(formData.option_d) && (
                <option value="d">D — {htmlToPlainPreview(formData.option_d)}</option>
              )}
              {!isRichTextEmpty(formData.option_e) && (
                <option value="e">E — {htmlToPlainPreview(formData.option_e)}</option>
              )}
            </select>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Explanation (Optional)</label>
            {isViewMode ? (
              <div 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                dangerouslySetInnerHTML={{ __html: formData.explanation }}
              />
            ) : (
              <>
               
                <div className="border border-gray-300 rounded-lg">
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.explanation}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setFormData(prev => ({ ...prev, explanation: data }));
                    }}
                    onReady={(editor) => {
                      explanationEditorRef.current = editor;
                    }}
                    config={{
                      placeholder: 'Provide an explanation for the answer...',
                      toolbar: classicToolbarConfig
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Use "Insert Math Equation" button to add mathematical formulas and expressions.
                </p>
              </>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {!isViewMode && (
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/questions')}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={submitting}
            >
              <FiX /> Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
              disabled={submitting}
            >
              <FiSave /> {submitting ? 'Saving...' : 'Save Question'}
            </button>
          </div>
        )}
      </form>

      {/* Math Equation Modal */}
      {showMathModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            
              <button
                onClick={() => setShowMathModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter LaTeX Math Expression:
                </label>
                <textarea
                  value={mathInput}
                  onChange={(e) => setMathInput(e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 font-mono"
                  placeholder="Example: x^2 + y^2 = z^2"
                  autoFocus
                />
              </div>

              {/* Preview */}
              {mathInput && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                  <div 
                    className="text-center text-lg"
                    dangerouslySetInnerHTML={{ __html: `\\(${mathInput}\\)` }}
                  />
                </div>
              )}

              {/* Common Examples */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-blue-900 mb-2">Quick Examples:</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-blue-800">
                  <button type="button" onClick={() => setMathInput('x^2 + y^2 = z^2')} className="text-left hover:text-blue-600">• Squares: x^2 + y^2 = z^2</button>
                  <button type="button" onClick={() => setMathInput('\\frac{a}{b}')} className="text-left hover:text-blue-600">• Fraction: \frac{'{'}a{'}{'}b{'}'}</button>
                  <button type="button" onClick={() => setMathInput('\\sqrt{x}')} className="text-left hover:text-blue-600">• Square root: \sqrt{'{'}x{'}'}</button>
                  <button type="button" onClick={() => setMathInput('\\int_a^b f(x)dx')} className="text-left hover:text-blue-600">• Integral: \int_a^b f(x)dx</button>
                  <button type="button" onClick={() => setMathInput('\\sum_{i=1}^{n} x_i')} className="text-left hover:text-blue-600">• Summation: \sum_{'{'}i=1{'}'}^{'{'}n{'}'} x_i</button>
                  <button type="button" onClick={() => setMathInput('\\lim_{x \\to \\infty} f(x)')} className="text-left hover:text-blue-600">• Limit: \lim_{'{'}x \to \infty{'}'} f(x)</button>
                  <button type="button" onClick={() => setMathInput('\\alpha, \\beta, \\gamma')} className="text-left hover:text-blue-600">• Greek: \alpha, \beta, \gamma</button>
                  <button type="button" onClick={() => setMathInput('\\leq, \\geq, \\neq')} className="text-left hover:text-blue-600">• Symbols: \leq, \geq, \neq</button>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowMathModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={insertMathEquation}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Insert Equation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddQuestion;
